import React, { useState, useEffect } from 'react';
import { V } from '../../../utils/theme';
import { Card, Btn, Skeleton, SuccessToastCtrl, ConfirmCtrl } from '../../../components/ui';
import { Notice, Eyebrow, Callout } from '../../../components/kit';
import { Icons } from '../../../components/Icons';
import { useApi } from './shared';
import { FLAG_GROUPS, flagState, flagEffective, flagDefault, flagPatch, mergeFlagPatch, resetFlagsPatch, explicitFlagCount, flagKeysFrom, unknownFlagKeys, plural } from './helpers';

// ─── Flags tab: org-wide feature switches as tri-state (default / on / off) ───
// get_flags {org_id} → { org: explicit map, effective, keys }; set_org_flags {org_id, flags}
// where null clears the explicit row.
//
// The confirm counts *clients* (role `member`) — the people whose app actually changes.
// It used to say "N members" with N = every active membership, so it promised the owner
// and the coaches would be affected too.
//
// `keys` is the API's own key list: any key it knows that this build has no label for is
// rendered in an "Other" group, so a flag added server-side is switchable without a
// member-app redeploy.
const STATES = [['default', 'Default'], ['on', 'On'], ['off', 'Off']];

function TriState({ value, onChange, disabled, label }) {
  return (
    <div role="radiogroup" aria-label={label} style={{ display: "flex", gap: 2, background: V.inputBg, borderRadius: 9, paddingTop: 2, paddingBottom: 2, paddingLeft: 2, paddingRight: 2, flexShrink: 0 }}>
      {STATES.map(([v, l]) => {
        const active = value === v;
        const color = v === 'off' ? V.danger : V.accent;
        return (
          <button key={v} type="button" role="radio" aria-checked={active} disabled={disabled} onClick={() => !disabled && !active && onChange(v)}
            style={{ fontSize: 11, fontWeight: 700, fontFamily: V.font, borderRadius: 7, border: "none", cursor: disabled || active ? "default" : "pointer", minWidth: 50, minHeight: 28,
              background: active ? (v === 'default' ? V.card : `${color}22`) : "transparent", color: active ? (v === 'default' ? V.text : color) : V.text3, opacity: disabled ? .6 : 1, WebkitTapHighlightColor: "transparent" }}>
            {l}
          </button>
        );
      })}
    </div>
  );
}

export function FlagsTab({ orgId, orgName, clientCount, staffCount }) {
  const api = useApi();
  const [org, setOrg] = useState(null); // explicit map
  const [keys, setKeys] = useState([]); // the API's key list
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(null);
  useEffect(() => {
    let dead = false;
    setOrg(null); setErr(''); setKeys([]);
    api('get_flags', { org_id: orgId }).then(r => {
      if (dead) return;
      if (r.ok) { setOrg(r.data.org || {}); setKeys(flagKeysFrom(r.data.keys, [r.data.effective, r.data.org])); }
      else { setOrg({}); setErr(r.msg); }
    });
    return () => { dead = true; };
  }, [orgId]); // eslint-disable-line react-hooks/exhaustive-deps

  const n = clientCount ?? 0;
  const staff = staffCount ?? 0;
  const affects = `This changes the app for ${plural(n, 'client')} of ${orgName} on their next open${staff ? ` (${plural(staff, 'staff member')} also see it in their own app)` : ''}. Per-client overrides still win.`;
  // Keys the API knows that this build has no group for — never silently dropped.
  const extra = unknownFlagKeys(keys);
  const write = async (patch, okMsg, key) => {
    setBusy(key); setErr('');
    const r = await api('set_org_flags', { org_id: orgId, flags: patch });
    if (r.ok) { setOrg(r.data.org || mergeFlagPatch(org, patch)); SuccessToastCtrl.show(okMsg); } else setErr(r.msg);
    setBusy(null);
  };
  const change = (key, label, state) => {
    const verb = state === 'default' ? `Reset ${label} to default (${flagDefault(key) ? 'on' : 'off'})` : `Turn ${label} ${state} for everyone`;
    ConfirmCtrl.show(`${verb}?`, affects, () => write(flagPatch(key, state), state === 'default' ? `${label} reset` : `${label} ${state}`, key));
  };
  const explicit = explicitFlagCount(org);
  const resetAll = () => ConfirmCtrl.show('Reset all features to defaults?', `${affects} All ${explicit} explicit switch${explicit === 1 ? '' : 'es'} go back to the IronLog defaults.`, () => write(resetFlagsPatch(org), 'Reset to defaults', 'all'));

  if (org === null) return <Card><Skeleton rows={8} /></Card>;
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="org-flags">
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <div style={{ flex: 1, minWidth: 160, fontSize: 11, color: V.text3 }} data-testid="org-flags-summary">{explicit} explicit switch{explicit === 1 ? '' : 'es'} · {plural(n, 'client')} affected{staff ? ` · ${plural(staff, 'staff member')}` : ''}</div>
        <Btn v="small" disabled={!explicit || busy === 'all'} onClick={resetAll}>Reset all to defaults</Btn>
      </div>
      {err && <Notice tone="error">{err}</Notice>}
      {[...FLAG_GROUPS, ...(extra.length ? [{ title: 'Other (new on the API)', keys: extra.map(k => [k, k, 'This app build has no description for this switch yet — it came from the API.']) }] : [])].map(g => (
        <Card key={g.title}>
          <Eyebrow style={{ marginBottom: 6 }}>{g.title}</Eyebrow>
          {g.keys.map(([key, label, desc], i) => {
            const state = flagState(org, key), eff = flagEffective(org, key);
            return (
              <div key={key} data-testid={`flag-${key}`} style={{ display: "flex", gap: 10, alignItems: "center", paddingTop: 8, paddingBottom: 8, borderTop: i ? `1px solid ${V.divider}` : 'none', opacity: busy === key ? .6 : 1 }}>
                <span aria-hidden style={{ width: 8, height: 8, borderRadius: 4, background: eff ? V.accent : V.danger, flexShrink: 0 }} title={eff ? 'Effective: on' : 'Effective: off'} />
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: V.text, display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap" }}>
                    {label}
                    {state === 'default' && <span style={{ fontSize: 9, color: V.text3, fontWeight: 600, textTransform: "uppercase", letterSpacing: ".06em", border: `1px solid ${V.cardBorder}`, borderRadius: 6, paddingLeft: 5, paddingRight: 5 }}>default {flagDefault(key) ? 'on' : 'off'}</span>}
                  </div>
                  <div style={{ fontSize: 10, color: V.text3, marginTop: 2, lineHeight: 1.4 }}>{desc}</div>
                </div>
                <TriState value={state} label={label} disabled={busy !== null} onChange={(s) => change(key, label, s)} />
              </div>
            );
          })}
        </Card>
      ))}
      <Callout icon={Icons.sliders}>Members see the change on their next app open. Per-client overrides (set by a coach in the portal) always win over these switches.</Callout>
    </div>
  );
}
